import React from "react";
import { ArrowRightOutlined, CopyOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Popconfirm, Space, Table, Tag, Typography } from "antd";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import { formatDateTime } from "../../../../../utils/dateUtils";

const STATUS_META = {
    DRAFT: { status: "processing", text: "草稿" },
    PARTIALLY_PUBLISHED: { status: "warning", text: "部分发布" },
    PUBLISHED: { status: "success", text: "发布完成" },
    ARCHIVED: { status: "default", text: "已归档" },
};

const SOURCE_META = {
    BLANK: { color: "blue", text: "空白航班计划" },
    FROM_V1: { color: "green", text: "旧版导入" },
    CLONE: { color: "purple", text: "复制航班计划" },
};

function formatPeriod(record) {
    if (!record.period_start && !record.period_end) {
        return "未设定";
    }

    return `${record.period_start || "未设定"} -> ${record.period_end || "未设定"}`;
}

function formatAircraftScope(aircraftScope = []) {
    if (!aircraftScope.length) {
        return "未设定";
    }

    if (aircraftScope.length <= 3) {
        return aircraftScope.join(", ");
    }

    return `${aircraftScope.slice(0, 3).join(", ")} +${aircraftScope.length - 3}`;
}

function renderAuditValue(name, time, emptyText) {
    if (!name && !time) {
        return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
    }

    return (
        <Space direction="vertical" size={0}>
            <Typography.Text>{name || "未记录人员"}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {time ? formatDateTime(time) : "未记录时间"}
            </Typography.Text>
        </Space>
    );
}

const ScheduleDraftListTable = ({
    tableRef,
    rowSelection,
    indexColumn,
    dataSource = [],
    total = 0,
    loading,
    pagination,
    onTableChange,
    onRow,
    onOpenDraft,
    onCopyDraftId,
    onDeleteDraft,
    deleteDraftLoading,
}) => {
    const columns = [
        ...(indexColumn ? [indexColumn] : []),
        {
            title: "航班计划",
            dataIndex: "name",
            key: "name",
            width: 260,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Typography.Link
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenDraft(record.id);
                        }}
                    >
                        {record.name || "未命名航班计划"}
                    </Typography.Link>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {record.description || "列表页用于进入航班计划编排。"}
                    </Typography.Text>
                </Space>
            ),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (_, record) => {
                const displayStatus = record.display_status || (record.status === "FINALIZED" ? "PUBLISHED" : record.status === "ARCHIVED" ? "ARCHIVED" : "DRAFT");
                const meta = STATUS_META[displayStatus] || { status: "default", text: displayStatus || "未知" };
                return <Badge status={meta.status} text={meta.text} />;
            },
        },
        {
            title: "来源",
            dataIndex: "source_type",
            key: "source_type",
            width: 130,
            render: (sourceType) => {
                const meta = SOURCE_META[sourceType] || { color: "default", text: sourceType || "未知" };
                return <Tag color={meta.color}>{meta.text}</Tag>;
            },
        },
        {
            title: "时间范围",
            key: "period",
            width: 220,
            render: (_, record) => formatPeriod(record),
        },
        {
            title: "飞机范围",
            dataIndex: "aircraft_scope",
            key: "aircraft_scope",
            width: 220,
            render: (aircraftScope) => formatAircraftScope(aircraftScope),
        },
        {
            title: "有效航班",
            dataIndex: "active_flight_count",
            key: "active_flight_count",
            width: 100,
            align: "right",
        },
        {
            title: "轨数",
            dataIndex: "track_count",
            key: "track_count",
            width: 90,
            align: "right",
        },
        {
            title: "最后编辑",
            key: "last_edited",
            width: 190,
            render: (_, record) => renderAuditValue(record.last_edited_by, record.last_edited_at, "未记录编辑"),
        },
        {
            title: "最近发布",
            key: "last_published",
            width: 190,
            render: (_, record) => renderAuditValue(record.last_published_by, record.last_published_at, "未发布"),
        },
        {
            title: "操作",
            key: "action",
            width: 220,
            fixed: "right",
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="schedule:read">
                        <Button
                            type="link"
                            size="small"
                            icon={<ArrowRightOutlined />}
                            onClick={(event) => {
                                event.stopPropagation();
                                onOpenDraft(record.id);
                            }}
                        >
                            进入
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="schedule:read">
                        <Button
                            type="link"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(event) => {
                                event.stopPropagation();
                                onCopyDraftId(record.id);
                            }}
                        >
                            复制 ID
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="schedule:delete">
                        <Popconfirm
                            title="确认删除"
                            description="确定要删除这个航班计划吗？"
                            onConfirm={(event) => {
                                event?.stopPropagation();
                                onDeleteDraft(record.id);
                            }}
                            okText="确认"
                            cancelText="取消"
                        >
                            <Button type="link" size="small" danger loading={deleteDraftLoading} onClick={(event) => event.stopPropagation()}>
                                删除
                            </Button>
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <Card className="data-card">
            <Table
                ref={tableRef}
                rowKey="id"
                size="small"
                rowSelection={rowSelection}
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                onChange={onTableChange}
                onRow={onRow}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (value) => `共 ${value} 个航班计划`,
                }}
                scroll={{ x: 1580 }}
            />
        </Card>
    );
};

export default ScheduleDraftListTable;
